import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, ShieldCheck, Plus, Home } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDateTime } from "@/lib/format";

const CATEGORIES = [
  { value: "poultry_house", label: "Poultry House" },
  { value: "brooding", label: "Brooding Area" },
  { value: "storage", label: "Storage" },
  { value: "medication", label: "Medication Storage" },
  { value: "processing", label: "Processing / Collection" },
  { value: "general", label: "General" },
];

export default function Property() {
  const [prop, setProp] = useState(null);
  const [subs, setSubs] = useState([]);
  async function refresh() {
    try {
      const [p, s] = await Promise.all([api.get("/property"), api.get("/property/sub-locations")]);
      setProp(p.data); setSubs(s.data);
    } catch { toast.error("Could not load property"); }
  }
  useEffect(() => { refresh(); }, []);

  if (!prop) return <div className="p-8"><div className="subtle">Loading property master…</div></div>;

  return (
    <div className="space-y-6" data-testid="property-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Master Data</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Property &amp; Location</h1>
        <p className="subtle mt-1">Single authoritative record for {prop.name}.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-2xl lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Home className="h-4 w-4 text-deepgreen" />{prop.name}</CardTitle>
            <CardDescription>{prop.property_type}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div><div className="subtle text-xs uppercase tracking-widest">Address</div><div className="font-medium">{prop.address_line1}</div></div>
              <div><div className="subtle text-xs uppercase tracking-widest">Town</div><div className="font-medium">{prop.town}</div></div>
              <div><div className="subtle text-xs uppercase tracking-widest">Province</div><div className="font-medium">{prop.province}</div></div>
              <div><div className="subtle text-xs uppercase tracking-widest">Country</div><div className="font-medium">{prop.country}</div></div>
              <div><div className="subtle text-xs uppercase tracking-widest">Latitude</div><div className="font-medium">{fmt(prop.latitude, 5)}</div></div>
              <div><div className="subtle text-xs uppercase tracking-widest">Longitude</div><div className="font-medium">{fmt(prop.longitude, 5)}</div></div>
              <div><div className="subtle text-xs uppercase tracking-widest">Verified</div><div className="font-medium">{prop.verified ? "Yes" : "No"}</div></div>
              <div><div className="subtle text-xs uppercase tracking-widest">Verified at</div><div className="font-medium">{prop.verified_at ? fmtDateTime(prop.verified_at) : "—"}</div></div>
              <div><div className="subtle text-xs uppercase tracking-widest">Status</div><div className="font-medium capitalize">{prop.status}</div></div>
            </div>
            <div className="mt-6 flex gap-3 flex-wrap">
              <EditDialog prop={prop} onSaved={refresh} />
              <VerifyDialog prop={prop} onSaved={refresh} />
              <a
                className="inline-flex items-center gap-1.5 text-sm rounded-lg border border-border px-3 py-1.5 hover:bg-muted"
                href={`https://maps.google.com/?q=${prop.latitude},${prop.longitude}`}
                target="_blank" rel="noreferrer" data-testid="property-directions"
              >
                <MapPin className="h-3.5 w-3.5" /> Open directions
              </a>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-deepgreen" />Verification history</CardTitle>
          </CardHeader>
          <CardContent>
            {(prop.verification_history || []).length === 0 ? (
              <div className="subtle text-sm">The map pin has not been verified yet.</div>
            ) : (
              <ul className="divide-y divide-border text-sm">
                {(prop.verification_history || []).slice().reverse().map((v) => (
                  <li key={v.id} className="py-2.5">
                    <div className="font-medium">{v.verified_by_username}</div>
                    <div className="subtle text-xs">{fmtDateTime(v.verified_at)}</div>
                    <div className="text-xs mt-1">{fmt(v.previous?.latitude, 5)}, {fmt(v.previous?.longitude, 5)} → {fmt(v.new?.latitude, 5)}, {fmt(v.new?.longitude, 5)}</div>
                    {v.reason && <div className="subtle text-xs mt-1">{v.reason}</div>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle>Sub-locations</CardTitle><CardDescription>{subs.length} configured</CardDescription></div>
          <NewSubLocation onCreated={refresh} />
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Active</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
            <TableBody>
              {subs.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell><Badge variant="outline">{s.category}</Badge></TableCell>
                  <TableCell>{s.is_active ? "Yes" : "No"}</TableCell>
                  <TableCell>{s.description}</TableCell>
                </TableRow>
              ))}
              {subs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center subtle py-8">No sub-locations yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function EditDialog({ prop, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...prop, reason: "" });
  useEffect(() => setForm({ ...prop, reason: "" }), [prop]);
  async function submit() {
    try {
      await api.patch("/property", {
        name: form.name, property_type: form.property_type,
        address_line1: form.address_line1, town: form.town, province: form.province, country: form.country,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
        reason: form.reason,
      });
      toast.success("Property updated"); setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="edit-property-btn">Edit</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit property master</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="col-span-2"><Label>Type</Label><Input value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} /></div>
          <div className="col-span-2"><Label>Address</Label><Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} /></div>
          <div><Label>Town</Label><Input value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} /></div>
          <div><Label>Province</Label><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} /></div>
          <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
          <div><Label>Latitude</Label><Input type="number" step="0.00001" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></div>
          <div><Label>Longitude</Label><Input type="number" step="0.00001" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></div>
          <div className="col-span-2"><Label>Reason for change</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-deepgreen hover:bg-deepgreen/90">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerifyDialog({ prop, onSaved }) {
  const [open, setOpen] = useState(false);
  const [lat, setLat] = useState(prop.latitude);
  const [lng, setLng] = useState(prop.longitude);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => { setLat(prop.latitude); setLng(prop.longitude); setConfirmed(false); }, [prop, open]);
  async function submit() {
    if (!confirmed) { toast.error("Please confirm the map pin"); return; }
    try {
      await api.post("/property/verify", { latitude: Number(lat), longitude: Number(lng), reason });
      toast.success("Property verified"); setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  const mapUrl = `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=17&size=520x260&maptype=osmarender&markers=${lat},${lng},red-pushpin`;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="verify-property-btn"><ShieldCheck className="h-4 w-4 mr-1" />Verify pin</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Verify Thom Homestead pin</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div className="rounded-xl overflow-hidden border border-border">
            <img src={mapUrl} alt="Map preview" className="w-full h-64 object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Latitude</Label><Input type="number" step="0.00001" value={lat} onChange={(e) => setLat(e.target.value)} data-testid="verify-lat" /></div>
            <div><Label>Longitude</Label><Input type="number" step="0.00001" value={lng} onChange={(e) => setLng(e.target.value)} data-testid="verify-lng" /></div>
          </div>
          <div><Label>Reason for adjustment (optional)</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} /></div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="verify-confirm" />
            <span>I confirm that this map pin is positioned on Thom Homestead and represents the correct operational location for Home360.</span>
          </label>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!confirmed} className="bg-deepgreen hover:bg-deepgreen/90" data-testid="verify-submit">Verify and activate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewSubLocation({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "general", description: "", is_active: true });
  async function submit() {
    try { await api.post("/property/sub-locations", form); toast.success("Sub-location added"); setOpen(false); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline"><Plus className="h-4 w-4 mr-1" />New sub-location</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New sub-location</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="col-span-2">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
